import { db } from "../db/indexeddb";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";

const normalize = (value) =>
  value?.toString().toLowerCase().replace(/\s+/g, " ").trim() || "";

const vendorSearchFields = [
  "company_name",
  "first_name",
  "last_name",
  "official_email_address",
  "company_official_email",
  "vendor_code",
  "role",
  "official_mobile_number",
  "invite_email_address",
];

const VENDOR_DOCUMENT_FIELDS = [
  "business_reg_certificate",
  "pan_card",
  "one_cancelled_cheque_leaf",
  "tax_registration_certificate",
  "msme_certificate",
  "tds_certificate",
  "corporate_identity_certificate",
];

const formatVendorMasterResult = (item = {}) => {
  const users = item.users || {};
  const admin = item.admin || {};
  const vendorDocuments = item.vendor_documents || {};

  const merged = {
    ...users,
    ...admin,
    users,
    admin: Object.keys(admin).length ? admin : undefined,
    tds_details: item.tds_details || {},
    vendor_documents: Object.keys(vendorDocuments).length ? vendorDocuments : undefined,
  };

  VENDOR_DOCUMENT_FIELDS.forEach((field) => {
    const url = vendorDocuments[field] || admin[field] || users[field];
    if (url) {
      merged[field] = url;
    }
  });

  return merged;
};

export class VendorMasterRepository {
  constructor() {
    this.inflightSyncRequests = new Map();
  }

  /* ==============================
     GET FROM CACHE
  ============================== */
  async getFromCache(org_token, searchQuery = "") {
    const data = await db.vendorMaster
      .where("org_token")
      .equals(org_token)
      .toArray();

    let records = data.map((d) => d.data);

    if (searchQuery && searchQuery.trim() !== "") {
      const q = normalize(searchQuery);
      records = records.filter((vendor) =>
        vendorSearchFields.some((field) =>
          normalize(vendor?.[field]).includes(q)
        )
      );
    }

    return records;
  }

  /* ==============================
     SAVE FULL LIST TO CACHE
  ============================== */
  async saveToCache(org_token, vendors = []) {
    if (!vendors.length) return;

    const rows = vendors.map((item) => ({
      token: item.token,
      org_token,
      company_name: item.company_name,
      created_on: item.created_on,
      data: item,
      lastSynced: Date.now(),
    }));

    await db.vendorMaster.bulkPut(rows);
  }

  /* ==============================
     SYNC FROM API
  ============================== */
  async syncFromAPI(
    authToken,
    org_token,
    pageNumber = 1,
    searchQuery = "",
    sortKey = "",
    sortOrder = "",
  ) {
    const q = searchQuery?.trim() || "";
    const requestKey = `${authToken || ""}|${org_token || ""}|${pageNumber || 1}|${q}|${sortKey || ""}|${sortOrder || ""}`;

    if (this.inflightSyncRequests.has(requestKey)) {
      return this.inflightSyncRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      const payload = {
        org_token,
        page: pageNumber,
      };

      if (q) {
        payload.search = q;
        payload.searchQuery = q;
      }

      if (sortKey) payload.sort_key = sortKey;
      if (sortOrder) payload.sort_order = sortOrder;

      const response = await axios.post(API_ENDPOINT.vendor_master_full_view, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const results = response?.data?.results || [];
      const formatted = results.map((item) => formatVendorMasterResult(item));

      this.saveToCache(org_token, formatted).catch((error) => {
        console.warn("Vendor cache update failed:", error);
      });

      return {
        data: formatted,
        total_count: response?.data?.total_count || 0,
        total_pages: response?.data?.total_pages || 0,
      };
    })();

    this.inflightSyncRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.inflightSyncRequests.delete(requestKey);
    }
  }

  async getSyncMeta(org_token) {
    return await db.vendorSyncMeta.get(org_token);
  }

  async setSyncMeta(org_token, lastSyncedPage, totalPages) {
    await db.vendorSyncMeta.put({
      org_token,
      lastSyncedPage,
      totalPages,
    });
  }

  /* ==============================
     SMART SYNC
  ============================== */
 async smartSync(
  authToken,
  org_token,
  pageNumber = 1,
  searchQuery = "",
  sortKey = "",
  sortOrder = "",
 ) {

  // Load current page first
  const currentPage = await this.syncFromAPI(
    authToken,
    org_token,
    pageNumber,
    searchQuery,
    sortKey,
    sortOrder
  );

  const totalPages = currentPage.total_pages || 1;

  // Background sync remaining pages
  (async () => {
    const meta = await this.getSyncMeta(org_token);
    const lastSyncedPage = meta?.lastSyncedPage || 1;
    for (let p = lastSyncedPage + 1; p <= totalPages; p++) {
      if (p === pageNumber) continue; // avoid duplicate
      await this.syncFromAPI(authToken, org_token, p, searchQuery, sortKey, sortOrder);
      await this.setSyncMeta(org_token, p, totalPages);
    }
  })().catch((error) => {
    console.warn("Vendor background sync failed:", error);
  });

  return currentPage;
}
}

export const vendorMasterRepository = new VendorMasterRepository();
